/**
 * The footer: Anuluj on the left; „Zapisz i uzupełnij później", Wstecz and ONE ember primary on the
 * right, and the reason the primary will not act written above it.
 *
 * ── One solid button, and every other control is text ───────────────────────────────────────
 * Build spec rule 5. The primary is `shared/ui/emberButton` — the one accent on this dialog.
 *
 * ── A blocked primary states its reason, and stays pressable ────────────────────────────────
 * Rule 6 and view ADR §12: never a dead grey button. The sentence is drawn above the primary — the
 * canon's `kp-gate` — and the primary is `aria-disabled` and described by it: before the last step
 * it names what the OPEN step still needs („Zanim przejdziesz dalej: wpisz nazwę.”, D19), on the
 * last step everything the save needs. It is said only after a press was refused on the open step
 * (polish of 4 November 2026): a pristine form is not scolded.
 * `aria-disabled` rather than the attribute, so the reason stays in the tab order, and a press
 * takes the trainer to the earliest step that fixes it (`useCreateExercise.submit`).
 *
 * The line is NOT reserved, which is the canon's own drawing (`kp-gate` is empty and zero-high on a
 * complete draft). The footer grows upward by one line while something is missing; the primary is
 * pinned to the dialog's bottom edge either way, so „Dalej" still never moves (D11).
 *
 * „Zapisz i uzupełnij później" saves from any step — the rest is optional, which is the point of it
 * — and is refused the same way the primary is.
 *
 * ── While a save runs, the gate says what it is waiting for ─────────────────────────────────
 * The rail and the step are `inert` then (`useCreateExercise`'s header says why), and the gate's
 * sentence — „Przygotowuję film, 40%.”, „Zapisuję ćwiczenie.”, „Wysyłam film, 40%.” — is the
 * reason nothing above it answers.
 *
 * ── ONE secondary button, four meanings ─────────────────────────────────────────────────────
 * Outside a save it is „Anuluj”. While the save still waits for a film or a cover it is „Przerwij
 * zapis”: nothing has been written, the wait ends, and focus goes back to the button that started
 * the save. While the movement is written it is „Anuluj”, refused and described by the gate. While
 * the film goes up it is „Przerwij wysyłanie”. `model/saveProgress` picks which; it is ONE element
 * whose label and handler change, so a phase changing under a focused button never drops focus to
 * the page.
 *
 * ── The film's milestones are SPOKEN, its percentages are not ───────────────────────────────
 * `shared/ui/LiveRegionAnnouncer`, outside everything that goes inert, says when a film starts
 * being prepared, what came of it, and when it starts going up (`model/saveProgress`). A refusal
 * that ends a save is said by the alert alone.
 */

import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import ember from '~/shared/ui/emberButton.module.css';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import { videoRefusalKey } from '~/shared/media/video/videoFile';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { CreateExerciseState } from '../lib/useCreateExercise';
import { saveProgress, type Phrase, type WizardExit } from '../model/saveProgress';

import styles from './CreateExerciseDialog.module.css';

interface WizardFooterProps {
  readonly wizard: CreateExerciseState;
  readonly onCancel: () => void;
}

const EXIT_LABEL: Readonly<Record<WizardExit, string>> = {
  cancel: 'web:exerciseLibrary.creator.footer.cancel',
  cancelRefused: 'web:exerciseLibrary.creator.footer.cancel',
  stopSave: 'web:exerciseLibrary.creator.footer.stopSave',
  stopUpload: 'web:exerciseLibrary.creator.footer.stopUpload',
};

export function WizardFooter({ wizard, onCancel }: WizardFooterProps) {
  const { t } = useTranslation();
  const gateId = useId();
  /* The button that started the running save, for „Przerwij zapis” to hand focus back to. Written
   * in the press's handler, read in the stop's. */
  const savePressed = useRef<HTMLButtonElement | null>(null);
  const say = (phrase: Phrase) =>
    t(phrase.key, {
      ...phrase.values,
      ...(phrase.reasonKey === undefined ? {} : { reason: t(phrase.reasonKey) }),
    });
  const progress = saveProgress({
    savePhase: wizard.savePhase,
    coverPreparing: wizard.coverPreparing,
    videoPreparingPercent: wizard.videoPreparingPercent,
    videoUploadPercent: wizard.videoUploadPercent,
    videoConfirming: wizard.videoConfirming,
    videoRefusal:
      wizard.videoRefusal === null
        ? null
        : {
            reasonKey: videoRefusalKey(wizard.videoRefusal.refusal),
            fileName: wizard.videoRefusal.fileName,
          },
    chosenVideoName: wizard.draft.video?.file.name ?? null,
    saveErrorKey: wizard.saveErrorKey,
  });

  // Nothing until a press was refused on this step; then the open step's own blockers, or on the
  // last step everything the save needs (`model/draftValidation`'s `gateBlockers`).
  const missing = wizard.gateBlockers.map((blocker) =>
    t(`web:exerciseLibrary.creator.footer.blocker.${blocker}`),
  );
  const list =
    missing.length < 2
      ? missing.join('')
      : `${missing.slice(0, -1).join(', ')} ${t('web:exerciseLibrary.creator.footer.and')} ${missing.at(-1) ?? ''}`;
  const gate = progress.busyLine !== null
    ? say(progress.busyLine)
    : wizard.saveErrorKey !== null
      ? t(wizard.saveErrorKey)
      : missing.length === 0
        ? ''
        : t(
            wizard.onLastStep
              ? 'web:exerciseLibrary.creator.footer.gateSave'
              : 'web:exerciseLibrary.creator.footer.gateNext',
            { list },
          );

  const saveRefused = wizard.blockers.length > 0 || wizard.busy;
  const exitRefused = progress.exit === 'cancelRefused';
  const startSave = (button: HTMLButtonElement) => {
    savePressed.current = button;
    wizard.submit();
  };
  const primaryRefused = wizard.onLastStep
    ? saveRefused
    : wizard.busy || wizard.stepBlockers.length > 0;

  return (
    <div className={styles.footWrap}>
      <p
        id={gateId}
        className={`${String(styles.gate)} ${wizard.saveErrorKey === null ? '' : String(styles.gateDanger)}`}
      >
        {gate}
      </p>
      {/* The save's failure, ASSERTIVE, in a region that is always mounted: an alert inserted
        * together with its text is one some screen readers never announce. The line above draws the
        * same sentence for the eye; it is not live, because it also names the blockers as the
        * trainer types and would chatter. */}
      <p className={hidden.srOnly} role="alert">
        {wizard.saveErrorKey === null ? '' : t(wizard.saveErrorKey)}
      </p>
      <LiveRegionAnnouncer message={progress.milestone === null ? '' : say(progress.milestone)} />
      <div className={styles.foot}>
        <button
          type="button"
          className={styles.textButton}
          aria-disabled={exitRefused ? 'true' : undefined}
          aria-describedby={exitRefused ? gateId : undefined}
          onClick={() => {
            switch (progress.exit) {
              case 'cancel':
                onCancel();
                return;
              case 'stopSave':
                wizard.stopSave();
                savePressed.current?.focus();
                return;
              case 'stopUpload':
                wizard.stopVideoUpload();
                return;
              case 'cancelRefused':
                // A save in flight is not abandoned by closing over it (ConfirmDialog's busy rule).
                return;
            }
          }}
        >
          {t(EXIT_LABEL[progress.exit])}
        </button>
        <span className={styles.spacer} />
        <button
          type="button"
          className={styles.textButton}
          aria-disabled={saveRefused ? 'true' : undefined}
          aria-describedby={gate === '' ? undefined : gateId}
          onClick={(event) => {
            if (!wizard.busy) startSave(event.currentTarget);
          }}
        >
          {t('web:exerciseLibrary.creator.footer.saveLater')}
        </button>
        {wizard.step !== 'basics' && (
          <button type="button" className={styles.textButton} onClick={wizard.back}>
            {t('exerciseLibrary.createWizard.back')}
          </button>
        )}
        <button
          type="button"
          className={`${String(ember.ember)} ${String(styles.primary)}`}
          aria-disabled={primaryRefused ? 'true' : undefined}
          aria-describedby={gate === '' ? undefined : gateId}
          onClick={(event) => {
            if (wizard.busy) return;
            if (wizard.onLastStep) startSave(event.currentTarget);
            else wizard.next();
          }}
        >
          {!wizard.onLastStep
            ? t('exerciseLibrary.createWizard.cta.next')
            : wizard.mode === 'edit'
              ? t('exerciseLibrary.editWizard.saveCta')
              : t('exerciseLibrary.createWizard.cta.save')}
        </button>
      </div>
    </div>
  );
}
